import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { TallyCountStepper } from '~/components/ui/TallyCountStepper';
import { sortedByLength } from '~/components/common/tallyBundleCounts';
import { formatQuantityCell, productLabel } from '~/utils';
import {
  conflictsOfRow,
  type SelectionConflict,
  type SelectionConflictsByRowId,
} from '~/pages/buy-lists/sections/selectionConflicts';
import {
  SelectableRow,
  SelectableRowLabel,
} from '~/pages/buy-lists/sections/SelectableRow';
import { SelectionConflictMessages } from '~/pages/buy-lists/sections/SelectionConflictMessages';
import { tallyQuantityAtCount } from './tallyQuantityAtCount';
import { useTallyLabel } from './useTallyLabel';
import type { BuyListDetailTally } from './types';

export type SelectBuyListTalliesSectionProps = {
  tallies: BuyListDetailTally[];
  selectedTallyIds: Set<string>;
  onToggleTally: (tallyId: string) => void;
  tallyCountFor: (tally: BuyListDetailTally) => number | null;
  onTallyCountChange: (tallyId: string, tallyCount: number) => void;
  conflictsByRowId: SelectionConflictsByRowId;
};

type TallySelectionRowProps = {
  tally: BuyListDetailTally;
  isSelected: boolean;
  tallyCount: number | null;
  conflicts: SelectionConflict[];
  onToggleTally: (tallyId: string) => void;
  onTallyCountChange: (tallyId: string, tallyCount: number) => void;
};

function TallySelectionRow({
  tally,
  isSelected,
  tallyCount,
  conflicts,
  onToggleTally,
  onTallyCountChange,
}: Readonly<TallySelectionRowProps>) {
  const { t } = useTranslation('inventory');
  const tallyLabel = useTallyLabel();
  const countInputId = useId();
  const quantity = tallyQuantityAtCount(tally, tallyCount);
  const lengthLabels = sortedByLength(tally.bundles)
    .map((bundle) => bundle.platformProductsLength.lengthDisplayValue)
    .join(', ');
  const checkboxId = `tally-selection-${tally.id}`;
  const conflictsId = `${checkboxId}-conflicts`;
  const hasConflicts = conflicts.length > 0;

  return (
    <SelectableRow
      testId={`tally-selection-row-${tally.id}`}
      isSelected={isSelected}
      layoutClassName="flex flex-col gap-2"
    >
      <div className="flex items-center gap-4">
        <SelectableRowLabel
          checkboxId={checkboxId}
          describedById={hasConflicts ? conflictsId : undefined}
          isSelected={isSelected}
          onToggle={() => onToggleTally(tally.id)}
        >
          <span className="truncate text-sm font-semibold text-neut-prim">
            {productLabel(tally.platformProduct)}
          </span>
          <span
            className="truncate text-xs text-neut-sec"
            data-testid={`tally-selection-label-${tally.id}`}
          >
            {tallyLabel(tally, tallyCount)}
          </span>
          <span
            className="truncate text-xs text-neut-sec"
            data-testid={`tally-selection-lengths-${tally.id}`}
          >
            {lengthLabels}
          </span>
        </SelectableRowLabel>
        <span
          className="shrink-0 text-sm font-semibold text-neut-prim"
          data-testid={`tally-selection-quantity-${tally.id}`}
        >
          {formatQuantityCell(quantity.value, quantity.unit)}
        </span>
        {tallyCount !== null && (
          <div
            className="shrink-0"
            data-testid={`tally-selection-count-${tally.id}`}
          >
            <label htmlFor={countInputId} className="sr-only">
              {t('inventory.buy_list.tallies.tally_count')}
            </label>
            <TallyCountStepper
              inputId={countInputId}
              value={tallyCount}
              disabled={!isSelected}
              onChange={(nextTallyCount) =>
                onTallyCountChange(tally.id, nextTallyCount)
              }
            />
          </div>
        )}
      </div>
      {hasConflicts && (
        <SelectionConflictMessages
          id={conflictsId}
          testId={`tally-selection-conflict-${tally.id}`}
          conflicts={conflicts}
        />
      )}
    </SelectableRow>
  );
}

export function SelectBuyListTalliesSection({
  tallies,
  selectedTallyIds,
  onToggleTally,
  tallyCountFor,
  onTallyCountChange,
  conflictsByRowId,
}: Readonly<SelectBuyListTalliesSectionProps>) {
  const { t } = useTranslation('inventory');

  if (tallies.length === 0) return null;

  return (
    <div data-testid="buy-list-tally-selection">
      <SectionPanel
        title={t('inventory.buy_list.tally_selection.title')}
        bodyClassName="flex flex-col gap-2 p-3"
      >
        <ul className="flex flex-col gap-2">
          {tallies.map((tally) => (
            <li key={tally.id}>
              <TallySelectionRow
                tally={tally}
                isSelected={selectedTallyIds.has(tally.id)}
                tallyCount={tallyCountFor(tally)}
                conflicts={conflictsOfRow(conflictsByRowId, tally.id)}
                onToggleTally={onToggleTally}
                onTallyCountChange={onTallyCountChange}
              />
            </li>
          ))}
        </ul>
      </SectionPanel>
    </div>
  );
}
